import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { files } from 'codemash';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { FormButton } from 'components/buttons/formButton';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { TranslationFieldOptions, getValidationShapeForTranslatableText } from 'utils/schemaHelper';
import { renderTime } from 'utils/dayTimeHelpers';
import { apiActionChooser } from 'utils/apiActionChooser';
import { postFile } from 'api/client/requests';

export const RestaurantsAddEditFormDineIn = ({ itemData }) => {
  const navigate = useNavigate();

  const { editById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [bannerImage, setBannerImage] = useState([]);
  const [logoImage, setLogoImage] = useState([]);

  const [loading, setLoading] = useState(false);

  const convertMinutesFromScheduleToTimeString = (schedule, field) => {
    const mins = schedule && schedule.length > 0 ? schedule[0][field] : undefined;

    if (mins === null || mins === undefined) {
      return '';
    }

    const hours = Math.trunc(mins / 60);
    const minutes = mins % 60;

    return renderTime(hours, minutes);
  };

  const convertTimeStringToMinutes = (timeString) => {
    if (!timeString) {
      return undefined;
    }

    const timeParts = timeString.split(':');

    if (!timeParts || timeParts.length !== 2) {
      return undefined;
    }

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return undefined;
    }

    return hours * 60 + minutes;
  };

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    review_link: yup.string().default(itemData?.review_link),
    allow_tips: yup.boolean().default(itemData?.allow_tips || false),
    allow_tips_alone: yup.boolean().default(itemData?.allow_tips_alone || false),
    allow_reviews_alone: yup.boolean().default(itemData?.allow_reviews_alone || false),
    // can_take_out: yup.boolean().default(itemData?.can_take_out || false),
    force_disable_tips: yup.boolean().default(itemData?.force_disable_tips || false),
    can_select_waiter: yup.boolean().default(itemData?.can_select_waiter || false),
    must_select_waiter: yup.boolean().default(itemData?.must_select_waiter || false),
    dont_require_guests: yup.boolean().default(itemData?.dont_require_guests || false),

    mon_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'mon_from')),
    mon_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'mon_to')),
    tue_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'tue_from')),
    tue_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'tue_to')),
    wed_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'wed_from')),
    wed_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'wed_to')),
    thu_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'thu_from')),
    thu_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'thu_to')),
    fri_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'fri_from')),
    fri_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'fri_to')),
    sat_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'sat_from')),
    sat_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'sat_to')),
    sun_from: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'sun_from')),
    sun_to: yup.string().default(convertMinutesFromScheduleToTimeString(itemData?.schedule, 'sun_to')),

    client_home_title: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.client_home_title,
          TranslationFieldOptions.OPTIONAL,
        ),
      ),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData?.banner) {
      setBannerImage([{ data_url: formFileUrlFromFileArray(itemData?.banner, true) }]);
    }

    if (itemData?.logo) {
      setLogoImage([{ data_url: formFileUrlFromFileArray(itemData?.logo, true) }]);
    }

    reset(itemData);
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    setLoading(true);

    const sumbitData = {
      review_link: data.review_link,
      allow_tips: data.allow_tips,
      allow_tips_alone: data.allow_tips_alone,
      allow_reviews_alone: data.allow_reviews_alone,
      force_disable_tips: data.force_disable_tips,
      can_select_waiter: data.can_select_waiter,
      must_select_waiter: data.must_select_waiter,
      dont_require_guests: data.dont_require_guests,
      client_home_title: data.client_home_title,
      schedule: [{
        mon_from: convertTimeStringToMinutes(data.mon_from),
        mon_to: convertTimeStringToMinutes(data.mon_to),
        tue_from: convertTimeStringToMinutes(data.tue_from),
        tue_to: convertTimeStringToMinutes(data.tue_to),
        wed_from: convertTimeStringToMinutes(data.wed_from),
        wed_to: convertTimeStringToMinutes(data.wed_to),
        thu_from: convertTimeStringToMinutes(data.thu_from),
        thu_to: convertTimeStringToMinutes(data.thu_to),
        fri_from: convertTimeStringToMinutes(data.fri_from),
        fri_to: convertTimeStringToMinutes(data.fri_to),
        sat_from: convertTimeStringToMinutes(data.sat_from),
        sat_to: convertTimeStringToMinutes(data.sat_to),
        sun_from: convertTimeStringToMinutes(data.sun_from),
        sun_to: convertTimeStringToMinutes(data.sun_to),
      }],
    };

    await apiActionChooser(auth.company?.company, async () => {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
          id: data._id,
          updateClause: { $set: sumbitData },
          token: auth.token,
        },
        async () => {
          const imagesUpdateQuery = { $set: {} };
          let anyImageDeleted = false;

          if (bannerImage && bannerImage.length > 0) {
            if (bannerImage[0].file !== null && bannerImage[0].file !== undefined) {
              await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: data._id,
                uniqueFieldName: 'banner',
                secretKey: auth.token,
                file: bannerImage[0].file,
              });
            }
          } else {
            imagesUpdateQuery.$set.banner = null;
            anyImageDeleted = true;
          }

          if (logoImage && logoImage.length > 0) {
            if (logoImage[0].file !== null && logoImage[0].file !== undefined) {
              await files.uploadRecordFile({
                collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
                database: Config.CODEMASH.DATABASE_ID,
                recordId: data._id,
                uniqueFieldName: 'logo',
                secretKey: auth.token,
                file: logoImage[0].file,
              });
            }
          } else {
            imagesUpdateQuery.$set.logo = null;
            anyImageDeleted = true;
          }

          if (anyImageDeleted) {
            await editById({
              collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
              id: data._id,
              updateClause: imagesUpdateQuery,
              token: auth.token,
            });
          }

          getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company, refresh: true });
          alertController.success({ text: 'Dine-in settings updated' });
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    }, async () => {
      let deleteBannerImage = false;
      let deleteLogoImage = false;

      if (bannerImage && bannerImage.length > 0) {
        if (bannerImage[0].file !== null && bannerImage[0].file !== undefined) {
          const fileResp = await postFile({
            fieldPath: 'banner',
            file: bannerImage[0].file,
            resource: 'RestaurantImages',
          });

          sumbitData.banner = fileResp.id;
        }
      } else {
        deleteBannerImage = true;
      }

      if (logoImage && logoImage.length > 0) {
        if (logoImage[0].file !== null && logoImage[0].file !== undefined) {
          const fileResp = await postFile({
            fieldPath: 'logo',
            file: logoImage[0].file,
            resource: 'RestaurantImages',
          });

          sumbitData.logo = fileResp.id;
        }
      } else {
        deleteLogoImage = true;
      }

      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
          id: data._id,
          updateClause: { $set: sumbitData },
          token: auth.token,
          resourceV2: 'AdminRestaurant',
          updateTypeV2: 'DineIn',
          updateDataV2: { ...sumbitData, deleteBannerImage, deleteLogoImage },
          companyIdForV2: auth.company?.company,
        },
        async () => {
          getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company, refresh: true });
          alertController.success({ text: 'Dine-in settings updated' });
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    });

    // await editById(
    //   {
    //     collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    //     id: data._id,
    //     updateClause: { $set: sumbitData },
    //     token: auth.token,
    //   },
    //   async () => {
    //     const imagesUpdateQuery = { $set: {} };
    //     let anyImageDeleted = false;

    //     if (bannerImage && bannerImage.length > 0) {
    //       if (bannerImage[0].file !== null && bannerImage[0].file !== undefined) {
    //         await files.uploadRecordFile({
    //           collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    //           database: Config.CODEMASH.DATABASE_ID,
    //           recordId: data._id,
    //           uniqueFieldName: 'banner',
    //           secretKey: auth.token,
    //           file: bannerImage[0].file,
    //         });
    //       }
    //     } else {
    //       imagesUpdateQuery.$set.banner = null;
    //       anyImageDeleted = true;
    //     }

    //     if (logoImage && logoImage.length > 0) {
    //       if (logoImage[0].file !== null && logoImage[0].file !== undefined) {
    //         await files.uploadRecordFile({
    //           collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    //           database: Config.CODEMASH.DATABASE_ID,
    //           recordId: data._id,
    //           uniqueFieldName: 'logo',
    //           secretKey: auth.token,
    //           file: logoImage[0].file,
    //         });
    //       }
    //     } else {
    //       imagesUpdateQuery.$set.logo = null;
    //       anyImageDeleted = true;
    //     }

    //     if (anyImageDeleted) {
    //       await editById({
    //         collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    //         id: data._id,
    //         updateClause: imagesUpdateQuery,
    //         token: auth.token,
    //       });
    //     }

    //     getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company, refresh: true });
    //     alertController.success({ text: 'Dine-in settings updated' });
    //     setLoading(false);
    //   },
    //   () => {
    //     setLoading(false);
    //   },
    // );
  };

  const onError = (data) => console.log(data);

  const renderWorkDayHours = (dayName, fromField, toField) => {
    return (
      <div className="col-span-6 flex flex-row items-center flex-wrap sm:flex-nowrap">
        <div className="w-[100%] md:w-[50%] lg:w-[50%] xl:w-[30%]">
          <label className="block text-sm font-medium text-gray-700">
            {dayName}
          </label>
        </div>

        <div className="flex flex-row items-center">
          <TextboxWrapper
            register={register}
            name={fromField}
            error={errors ? errors[fromField] : null}
            responsiveClassName="col-span-6 sm:col-span-3"
            placeholder="HH:mm"
          />

          <div className="w-6 text-center">-</div>

          <TextboxWrapper
            register={register}
            name={toField}
            error={errors ? errors[toField] : null}
            responsiveClassName="col-span-6 sm:col-span-3"
            placeholder="HH:mm"
          />
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      <div className="shadow sm:rounded-md sm:overflow-hidden">
        <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Dine-in settings
            </h3>
          </div>

          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Banner</label>
              <ImageUpload
                id="bannerImage"
                name="bannerImage"
                images={bannerImage}
                onChange={(val) => setBannerImage(val)}
              />
            </div>
            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Logo</label>
              <ImageUpload
                id="logoImage"
                name="logoImage"
                images={logoImage}
                onChange={(val) => setLogoImage(val)}
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Tipping options</p>
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="allow_tips"
                title="Allow tips during order"
                subtitle="Allows leaving tips when making an order"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="allow_tips_alone"
                title="Allow tips without order"
                subtitle="Allows leaving tips without an order"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="force_disable_tips"
                title="Disable tips"
                subtitle="Disable tips from home screen"
                responsiveClassName="w-full"
              />
            </div>

            <div className="hidden sm:block sm:col-span-3" />

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="can_select_waiter"
                title="Client selects waiter"
                subtitle="Allows client to select waiter to tip"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="must_select_waiter"
                title="Client cannot tip all team"
                subtitle="Client must select waiter for a tip"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Work hours (dine-in)</p>
              <p className="text-gray-500 text-xs">
                By default, orders can be placed at any time. Providing work hours will prevent orders from being placed outside of work hours. To completely disable orders for a day, add the same time for both fields, for example: 00:00 - 00:00
              </p>
            </div>

            <div className="col-span-6 grid grid-cols-6 gap-4 sm:gap-2">
              {renderWorkDayHours('Monday', 'mon_from', 'mon_to')}
              {renderWorkDayHours('Tuesday', 'tue_from', 'tue_to')}
              {renderWorkDayHours('Wednesday', 'wed_from', 'wed_to')}
              {renderWorkDayHours('Thursday', 'thu_from', 'thu_to')}
              {renderWorkDayHours('Friday', 'fri_from', 'fri_to')}
              {renderWorkDayHours('Saturday', 'sat_from', 'sat_to')}
              {renderWorkDayHours('Sunday', 'sun_from', 'sun_to')}
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Reviews settings</p>
            </div>

            <div className="sm:col-span-4 col-span-6">
              <TextboxWrapper
                register={register}
                name="review_link"
                title="Review link"
                subtitle="If using external reviews instead of built-in"
                error={errors?.review_link}
                responsiveClassName="col-span-6 sm:col-span-4"
              />
            </div>

            <div className="hidden sm:block sm:col-span-2" />

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="allow_reviews_alone"
                title="Allow reviews without order"
                subtitle="Allows leaving reviews without an order"
                responsiveClassName="w-full"
              />
            </div>

            <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

            <div className="col-span-6">
              <p className="text-base font-medium text-gray-900">Display options</p>
            </div>

            <div className="sm:col-span-4 col-span-6">
              <TranslatableInput
                name="client_home_title"
                register={register}
                error={errors?.client_home_title}
                containerClassName="col-span-6 sm:col-span-4"
                labelClassName="block text-sm font-medium text-gray-700"
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Custom client home title"
                renderAsTextArea
              />
            </div>

            <div className="hidden sm:block sm:col-span-2" />

            <div className="col-span-6 sm:col-span-3">
              <CheckboxWrapper
                register={register}
                name="dont_require_guests"
                title="Don't ask guest count"
                subtitle="Guest count is enabled by default, you can disable it"
                responsiveClassName="w-full"
              />
            </div>

            {/* currentRestaurantType !== 'TipsOnly' && (
              <CheckboxWrapper
                register={register}
                name="can_take_out"
                title="Allow takeout orders"
                subtitle="Allows ordering for takeout"
                responsiveClassName="w-full mt-3"
              />
            ) */}

          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
          <button
            type="button"
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => navigate('/settings/restaurants')}
          >
            Cancel
          </button>
          <FormButton text="Save" isLoading={loading} />
        </div>
      </div>
    </form>
  );
};
